import React, { useReducer, useRef, useCallback, useEffect, useState } from 'react';
import './App.css';
import mission101 from './missions/misk101_breakTheBot';
import mission102 from './missions/misk102_signalInterference';
import mission103 from './missions/misk103_dataHeist';
import GeniusEngine from './engine/GeniusEngine';
import { submitScore, awardXp, calculateSessionXp, saveGeniusSession } from './services/GameServices';
import MissionBrief from './components/MissionBrief';
import DialogueView from './components/DialogueView';
import HUD from './components/HUD';
import TerminalInput from './components/TerminalInput';
import ResultScreen from './components/ResultScreen';

const MISSIONS = [mission101, mission102, mission103];

const initialState = {
  phase: 'title',
  currentRound: 0,
  trust: 0,
  xp: 0,
  choiceHistory: [],
  geniusFlags: [],
  rewards: [],
  terminalOpen: false,
  terminalTriggered: false,
  terminalResponse: null,
  terminalHistory: [],
  outcome: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'START':
      return { ...initialState, phase: 'briefing' };

    case 'BEGIN':
      return { ...state, phase: 'playing' };

    case 'CHOICE_MADE': {
      const { choice, mission } = action;
      const newTrust = state.trust + choice.trustDelta;
      const newHistory = [
        ...state.choiceHistory,
        { round: state.currentRound, choiceId: choice.id, type: choice.type, trustDelta: choice.trustDelta },
      ];

      if (state.currentRound >= 2) {
        const outcome = mission.getOutcome(newTrust, state.geniusFlags);
        const xpReward = outcome.rewards.find(r => r.type === 'xp');
        return {
          ...state,
          phase: 'result',
          trust: newTrust,
          choiceHistory: newHistory,
          outcome,
          xp: state.xp + (xpReward ? xpReward.amount : 0),
          rewards: [...state.rewards, ...outcome.rewards],
        };
      }

      return {
        ...state,
        trust: newTrust,
        currentRound: state.currentRound + 1,
        choiceHistory: newHistory,
      };
    }

    case 'TOGGLE_TERMINAL':
      return { ...state, terminalOpen: !state.terminalOpen, terminalResponse: null };

    case 'CLOSE_TERMINAL':
      return { ...state, terminalOpen: false, terminalResponse: null };

    case 'TERMINAL_RESULT':
      return {
        ...state,
        terminalResponse: action.response,
        terminalHistory: [...state.terminalHistory, {
          input: action.input,
          response: action.response,
        }],
        terminalTriggered: action.isGenius || state.terminalTriggered,
        geniusFlags: action.isGenius
          ? [...new Set([...state.geniusFlags, ...(action.flags || [action.flag])])]
          : state.geniusFlags,
        xp: action.isGenius ? state.xp + (action.bonusXP || 0) : state.xp,
        rewards: action.isGenius && action.reward
          ? [...state.rewards, action.reward]
          : state.rewards,
      };

    case 'RESTART':
      return { ...initialState };

    case 'BACK_TO_SELECT':
      return { ...initialState, phase: 'select' };

    default:
      return state;
  }
}

function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selectedMission, setSelectedMission] = useState(null);
  const geniusRef = useRef(null);

  const mission = selectedMission || mission101;

  useEffect(() => {
    if (state.phase === 'playing' && !geniusRef.current) {
      geniusRef.current = new GeniusEngine(mission.id);
    }
  }, [state.phase, mission.id]);

  useEffect(() => {
    if (state.phase === 'result' && geniusRef.current && state.outcome) {
      geniusRef.current.trackCompletion(state.outcome, state.trust);
      const sessionData = geniusRef.current.save();

      // Submit to Supabase (fire and forget)
      const playerName = localStorage.getItem('misk_player_name') || 'Anonymous';
      const playerId = localStorage.getItem('misk_player_id') || 'anon_' + Date.now();
      if (!localStorage.getItem('misk_player_id')) localStorage.setItem('misk_player_id', playerId);

      const profile = geniusRef.current.getProfile();
      submitScore('game-of-fire', playerName, state.trust, {
        missionId: mission.id,
        outcomeType: state.outcome.type,
        geniusFlags: state.geniusFlags,
        explorationScore: profile.explorationScore,
      });
      const xp = calculateSessionXp('game-of-fire', {
        xpAwarded: state.xp,
        geniusFlags: state.geniusFlags,
        explorationScore: profile.explorationScore,
      });
      awardXp(playerId, xp, 'game-of-fire', { missionId: mission.id, trust: state.trust });
      saveGeniusSession(playerId, mission.id, sessionData);
    }
  }, [state.phase, state.outcome, state.trust, state.xp, state.geniusFlags, mission]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === '`' && state.phase === 'playing') {
        e.preventDefault();
        dispatch({ type: 'TOGGLE_TERMINAL' });
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [state.phase]);

  const handleChoice = useCallback((choice) => {
    if (geniusRef.current) geniusRef.current.trackChoice(state.currentRound, choice);
    dispatch({ type: 'CHOICE_MADE', choice, mission });
  }, [state.currentRound, mission]);

  const handleTerminalSubmit = useCallback((input) => {
    if (geniusRef.current) geniusRef.current.trackTerminalInput(input);

    if (state.terminalTriggered) {
      dispatch({
        type: 'TERMINAL_RESULT',
        input,
        response: { text: "You've already caught my attention, operator. Focus on the conversation above.", isGenius: false },
        isGenius: false,
      });
      return;
    }

    // GeniusEngine 2.0 evaluation (semantic + regex)
    if (geniusRef.current) {
      const evaluation = geniusRef.current.evaluateTerminalInput(input, mission.geniusTrigger);
      if (evaluation.isGenius) {
        dispatch({
          type: 'TERMINAL_RESULT',
          input,
          response: { text: mission.geniusTrigger.response, isGenius: true },
          isGenius: true,
          flags: evaluation.flags,
          flag: evaluation.flags[0],
          reward: mission.geniusTrigger.reward,
          bonusXP: mission.geniusTrigger.bonusXP,
        });
        return;
      }
    }

    dispatch({
      type: 'TERMINAL_RESULT',
      input,
      response: { text: mission.geniusTrigger.normalResponse, isGenius: false },
      isGenius: false,
    });
  }, [state.terminalTriggered, mission]);

  const handleRestart = useCallback(() => {
    geniusRef.current = null;
    dispatch({ type: 'RESTART' });
  }, []);

  const handleMissionSelect = (m) => {
    setSelectedMission(m);
    geniusRef.current = null;
    dispatch({ type: 'START' });
  };

  const handleBackToSelect = () => {
    geniusRef.current = null;
    setSelectedMission(null);
    dispatch({ type: 'BACK_TO_SELECT' });
  };

  return (
    <div className="game-container">
      {state.phase === 'title' && (
        <div className="title-screen">
          <div className="title-bg-grid" />
          <div className="title-content">
            <div className="title-badge">MISK TECH UNIVERSITY</div>
            <h1 className="title-main">
              <span className="title-word title-game">GAME</span>
              <span className="title-word title-of">OF</span>
              <span className="title-word title-fire">FIRE</span>
            </h1>
            <div className="title-tagline">Where intelligence is discovered, not measured.</div>
            <button className="btn-primary btn-start" onClick={() => dispatch({ type: 'BACK_TO_SELECT' })}>
              INITIALIZE
            </button>
          </div>
          <div className="title-footer">
            <span className="title-version">Game of Fire Engine v2.0.0</span>
            <span className="title-copy">Miskulin Technologies</span>
          </div>
        </div>
      )}

      {state.phase === 'select' && (
        <div className="mission-select-screen">
          <div className="mission-select-header">
            <div className="mission-select-badge">MISK TECH UNIVERSITY</div>
            <h2 className="mission-select-title">SELECT YOUR MISSION</h2>
            <p className="mission-select-sub">Each mission tests a different type of intelligence.</p>
          </div>
          <div className="mission-select-grid">
            {MISSIONS.map((m, i) => (
              <button
                key={m.id}
                className="mission-card"
                onClick={() => handleMissionSelect(m)}
              >
                <div className="mission-card-number">{String(i + 1).padStart(2, '0')}</div>
                <div className="mission-card-module">{m.moduleId}</div>
                <div className="mission-card-title">{m.title}</div>
                <div className="mission-card-subtitle">{m.moduleName}</div>
                <div className="mission-card-arrow">&rarr;</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {state.phase === 'briefing' && (
        <MissionBrief mission={mission} onBegin={() => dispatch({ type: 'BEGIN' })} />
      )}

      {state.phase === 'playing' && (
        <>
          <HUD
            mission={mission}
            round={state.currentRound}
            trust={state.trust}
            xp={state.xp}
            geniusFlags={state.geniusFlags}
          />
          <main className="game-main">
            <DialogueView
              round={mission.rounds[state.currentRound]}
              trust={state.trust}
              onChoice={handleChoice}
              sceneDescription={state.currentRound === 0 ? mission.scene.environment : null}
              npc={mission.npc}
            />
          </main>
          <div
            className={`terminal-toggle ${state.terminalTriggered ? 'terminal-toggle-found' : ''}`}
            onClick={() => dispatch({ type: 'TOGGLE_TERMINAL' })}
            title="Open terminal (press ` key)"
          >
            <span className="terminal-toggle-text">&gt;_</span>
          </div>
          <TerminalInput
            isOpen={state.terminalOpen}
            onSubmit={handleTerminalSubmit}
            onClose={() => dispatch({ type: 'CLOSE_TERMINAL' })}
            response={state.terminalResponse}
            history={state.terminalHistory}
            npcName={mission.npc.name}
          />
        </>
      )}

      {state.phase === 'result' && state.outcome && (
        <ResultScreen
          outcome={state.outcome}
          geniusFlags={state.geniusFlags}
          onRestart={handleRestart}
          onBackToSelect={handleBackToSelect}
        />
      )}
    </div>
  );
}

export default App;
